import { Grid, Typography } from "@mui/material";
import {
  DropdownArrPropsType,
  FormObjType,
  HandleChangeType,
  HandleClickType,
  SingleSelectValType,
} from "DataTypes/Form";
import { ControlledBoxSizeSwitch, FormObjSwitch } from "Utils/SwitchCases";
import { ControlledForm } from "Components";
import { FC, Fragment, useEffect, useState } from "react";
import Format from "./Format";
import { AppointmentTypeType } from "DataTypes/PatientManagement.type";
import { FormatDropdownData } from "Utils/DataFormatter";
import {
  appointmentDateTime,
  AppointmentFormData,
  appointmentNotes,
  appointmentType,
  deviceType,
  deviceTypeName,
  orgName,
  patientSearch as patientSearchField,
  providerName,
  timezoneData,
} from "./Data";
import Loader from "Components/Loader";
import AppointmentApi from "Service/Appointment.api";
import DateTime from "Utils/DateTime";
import {
  AppointmentPatientType,
  AppointmentType,
} from "DataTypes/Services/Patient.type";
import { useDispatch } from "react-redux";
import { addSelectedPrgm } from "Redux/Patient/Action";
import { selectedPrgmInitialState } from "Redux/Patient/reducer";
import AppointmentFormatter from "Utils/Appointment.Formatter";
import TrainingApi from "Service/Training.api";
import TrainingFormatter from "Utils/Training.Formatter";
import { TrainingListType } from "DataTypes/Services/Training.type";
import { UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import CommonFormatter from "Utils/Common.Formatter";

interface AppointmentModalProps {
  setForm: (data: FormObjType[]) => void;
  editAppointment: AppointmentType | null;
  setIsReadOnly: (isReadOnly: boolean) => void;
}

const AppointmentModal: FC<AppointmentModalProps> = ({
  setForm,
  editAppointment,
  setIsReadOnly
}: AppointmentModalProps) => {
  const dispatch = useDispatch();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const [formArr, setFormArr] = useState<FormObjType[]>([]);
  const [patientList, setPatientList] = useState<AppointmentPatientType[]>([]);
  const [patientSearchText, setPatientSearchText] = useState("");
  const [patientDropArr, setPatientDropArr] =
    useState<DropdownArrPropsType[]>([]);
  const [patientDropVal, setPatientDropVal] =
    useState<SingleSelectValType>(null);
  const [appTypeArr, setAppTypeArr] = useState<AppointmentTypeType[]>([]);
  const [loading, setLoading] = useState(true);
  const orgInitObj = JSON.parse(JSON.stringify(orgName));
  const providerInitObj = JSON.parse(JSON.stringify(providerName));
  const appointmentDateInitObj =
    JSON.parse(JSON.stringify(appointmentDateTime));
  const timezoneInitObj = JSON.parse(JSON.stringify(timezoneData));
  const notesInitObj = JSON.parse(JSON.stringify(appointmentNotes));

  useEffect(() => {
    fetchPatientList();
  }, []);

  const syncFormState: (updated: FormObjType[]) => void = (updated) => {
    setFormArr(updated);
    setForm(updated);
  };

  const resetPatientDependentFields: (
    formData: FormObjType[]
  ) => FormObjType[] = (formData) => {
    const newFormData = formData.map((el) => ({ ...el }));
    newFormData[1] = JSON.parse(JSON.stringify(appointmentType));
    newFormData[2] = JSON.parse(JSON.stringify(deviceTypeName));
    newFormData[3] = JSON.parse(JSON.stringify(orgName));
    newFormData[4] = JSON.parse(JSON.stringify(providerName));
    newFormData[5] = JSON.parse(JSON.stringify(appointmentDateTime));
    newFormData[6] = JSON.parse(JSON.stringify(timezoneData));
    newFormData[7] = JSON.parse(JSON.stringify(appointmentNotes));
    return newFormData;
  };

  const handlePatientSearch: (
    searchVal?: string
  ) => Promise<void> = async (searchVal) => {
    const newSearchVal = searchVal || "";
    const res = await AppointmentApi.PatientList({
      data: { patientName: newSearchVal },
      offset: 0,
      limit: 20,
    });
    let newPatientDrop: DropdownArrPropsType[] = [];
    let patientArr: AppointmentPatientType[] = [];
    if (res?.success) {
      patientArr = Format.AppointmentPatientList(res?.data);
      newPatientDrop = FormatDropdownData(
        patientArr,
        "patientId",
        "name",
        "patientId"
      );
    }
    setPatientList(patientArr);
    setPatientDropArr(newPatientDrop);
  };

  const updatePatientInForm: (
    inputVal: string,
    dropVal: SingleSelectValType,
    resetDeps?: boolean
  ) => void = (inputVal, dropVal, resetDeps = false) => {
    setFormArr((prev) => {
      let updated = prev.map((el) => ({ ...el }));
      if (updated[0]?.boxType == "single-select") {
        updated[0].inputVal = inputVal;
        updated[0].dropVal = dropVal;
      }
      if (resetDeps) {
        updated = resetPatientDependentFields(updated);
      }
      setForm(updated);
      return updated;
    });
  };

  const fetchPatientList: () => void = async () => {
    let newFormData: FormObjType[] = JSON.parse(
      JSON.stringify(AppointmentFormData(editAppointment ? true : false))
    );
    if (editAppointment) {
      const typeRes = await AppointmentApi.Type({
        id1: editAppointment.patientId,
      });
      if (typeRes?.success) {
        const appTypeData = AppointmentFormatter.TypeArr(typeRes?.data);
        setAppTypeArr(appTypeData);
        if (newFormData[0].boxType == "input") {
          newFormData[0].inputVal =
            editAppointment.patientName || editAppointment.name;
          newFormData[0].readOnly = true;
        }
        if (newFormData[1].boxType == "input") {
          newFormData[1].inputVal =
            editAppointment.activity;
          newFormData[1].readOnly = true;
        }
        if (editAppointment.patientDeviceId) {
          newFormData[2].boxType = "input";
          newFormData[2].inputVal =
            editAppointment.deviceTypeDescription;
          newFormData[2].readOnly = true;
          newFormData[2].required = true
        }
        const selectedAppType = appTypeData.find(
          (el) => el.appointmentType == editAppointment.activity
        );
        if (newFormData[3].boxType == "input") {
          newFormData[3].inputVal = editAppointment.practice;
        }
        if (newFormData[4].boxType == "input") {
          newFormData[4].inputVal = editAppointment.provider;
        }
        const date = DateTime.UTCToLocalDateTime(
          editAppointment.datetime,
          editAppointment.appointmentTimzone
        )
        if (newFormData[5].boxType == "date-time") {
          newFormData[5].inputVal = date;
          if (selectedAppType?.isActivityCompletedThisMonth) {
            newFormData[5].minVal = DateTime.NextMonthStartDateTime();
          } else {
            newFormData[5].minVal = DateTime.CurrentDateTime();
          }
        }
        if (newFormData[6].boxType == "single-select") {
          newFormData[6].dropVal = newFormData[6].inputVal =
            editAppointment.appointmentTimzone;
        }
        if (newFormData[7].boxType == "input") {
          ///As per the task requirement 10450
          // newFormData[6].readOnly = true;
          newFormData[7].inputVal = editAppointment.notes;
        }
        if (!selectedAppType) {
          setIsReadOnly(true);
          newFormData =
            newFormData.map(formEl => ({ ...formEl, readOnly: true }));
        }
      }
    } else {
      await handlePatientSearch("");
    }
    if (newFormData[5].boxType == "date-time") {
      newFormData[5].maxVal =
        DateTime.NextYearDateTimeFromCurrentDate();
    }
    syncFormState(newFormData);
    setLoading(false);
  };

  const handlePatientSelect: (
    dropVal: SingleSelectValType
  ) => Promise<void> = async (dropVal) => {
    const selectedLabel =
      patientDropArr.find((findEl) => findEl.value == dropVal)?.label || "";
    setPatientSearchText(selectedLabel);
    setPatientDropVal(dropVal);

    const newFormData: FormObjType[] = JSON.parse(JSON.stringify(formArr));
    const find = patientList.find((el) => el.patientId == dropVal);
    console.log(find);
    let appTypeData: AppointmentTypeType[] = [];
    if (find && dropVal) {
      if (newFormData[0].boxType == "single-select") {
        newFormData[0].inputVal = selectedLabel;
        newFormData[0].dropVal = dropVal;
      }
      if (newFormData[3].boxType == "input") {
        newFormData[3].inputVal = find.practice;
      }
      if (newFormData[6].boxType == "single-select") {
        newFormData[6].dropVal = find.timezone;
        newFormData[6].inputVal = find.timezone;
      }
      const typeRes = await AppointmentApi.Type({
        id1: find.patientId,
      });
      if (typeRes?.success) {
        appTypeData = AppointmentFormatter.TypeArr(typeRes?.data);
      }
    } else {
      newFormData[3] = orgInitObj;
      newFormData[6] = timezoneInitObj
    }
    setAppTypeArr(appTypeData);
    if (newFormData[1].boxType == "single-select") {
      newFormData[1] = JSON.parse(JSON.stringify(appointmentType));
      if (newFormData[1].boxType == "single-select") {
        newFormData[1].dropArr = FormatDropdownData(
          appTypeData,
          "appointmentType",
          "appointmentType",
          "appointmentType"
        );
      }
    }
    newFormData[2] = JSON.parse(JSON.stringify(deviceTypeName));
    newFormData[4] = providerInitObj;
    newFormData[5] = appointmentDateInitObj;
    newFormData[7] = notesInitObj;

    const newArr = CommonFormatter.HandleClickChange(
      { id: "patient", dropVal, boxType: "single-select" },
      newFormData
    );
    syncFormState(newArr);
  };

  const localHandleClick: (
    props: HandleClickType
  ) => Promise<void> = async (props) => {
    const { id, dropVal, boxType } = props;
    if (id == "patient") {
      await handlePatientSelect(
        boxType == "single-select"
          ? (dropVal as SingleSelectValType)
          : null
      );
      return;
    }
    const newFormData: FormObjType[] = JSON.parse(JSON.stringify(formArr));
    if (id == "appointment-type") {
      const findType = appTypeArr.find((el) => el.appointmentType == dropVal);
      dispatch(
        addSelectedPrgm({
          ...selectedPrgmInitialState,
          ...findType,
        })
      );
      let provName = "";
      let trainingList: TrainingListType[] = [];
      if (findType && dropVal) {
        provName = findType.providerNameDes;
        if (findType.isRpmTraining) {
          const selectedPatientId =
            patientDropVal || (
              newFormData[0].boxType == "single-select" ?
                newFormData[0].dropVal : ""
            );
          const res = await TrainingApi.myTraininglist({
            data: {
              patientId: selectedPatientId,
              trainerId: myInfo?.id,
            },
            limit: 10,
            offset: 0,
          });
          if (res?.success) {
            trainingList = TrainingFormatter.TrainingList(res?.data);
          }
        }
      }
      if (newFormData[4].boxType == "input") {
        newFormData[4].inputVal = provName
      }
      if (trainingList.length > 0) {
        newFormData[2] = JSON.parse(JSON.stringify(deviceType));
        newFormData[2].readOnly = editAppointment ? true : false;
        newFormData[2].required = true;
        newFormData[2].boxType == "single-select" &&
          (newFormData[2].dropArr = FormatDropdownData(
            trainingList,
            "patientDeviceId",
            "deviceTypeDescription",
            "patientDeviceId"
          ));
      } else {
        newFormData[2] = JSON.parse(JSON.stringify(deviceTypeName));
        newFormData[2].readOnly = true;
        newFormData[2].required = false;
      }
      if (newFormData[5].boxType == "date-time") {
        if (findType?.isActivityCompletedThisMonth) {
          newFormData[5].minVal = DateTime.NextMonthStartDateTime();
        } else {
          newFormData[5].minVal = DateTime.CurrentDateTime();
        }
      }
    }
    const newArr = CommonFormatter.HandleClickChange(props, newFormData);
    syncFormState(newArr);
  };

  const handleInputChange: (
    props: HandleChangeType
  ) => Promise<void> = async (props) => {
    setFormArr((prev) => {
      const updated = CommonFormatter.HandleInputChange2(props, prev);
      setForm(updated);
      return updated;
    });
  };

  return (
    <Fragment>
      {loading ? (
        <Loader />
      ) : (
        <Fragment>
          <Grid container rowSpacing={1} columnSpacing={2}>
            {!editAppointment && (
              <Grid
                item
                {...ControlledBoxSizeSwitch(patientSearchField.boxSize)}
              >
                <ControlledForm.SingleApi
                  {...patientSearchField}
                  onClick={async (val) => handlePatientSelect(val)}
                  onChangeApiSearch={true}
                  inputChange={async (val) => {
                    setPatientSearchText(val);
                    setPatientDropVal(null);
                    updatePatientInForm(val, null, true);
                    handlePatientSearch(val);
                  }}
                  inputVal={patientSearchText}
                  dropArr={patientDropArr}
                  dropVal={patientDropVal}
                  apiSearch={true}
                  iconSearch={true}
                />
              </Grid>
            )}
            {formArr.map((el) => {
              if (el.boxType == "empty") return <Fragment key={el.id} />;
              if (!editAppointment && el.id == "patient") {
                return <Fragment key={el.id} />;
              }
              return (
                <Grid
                  key={el.id}
                  item
                  {...(el.boxSize && ControlledBoxSizeSwitch(el.boxSize))}
                >
                  {FormObjSwitch({
                    formObj: el,
                    inputChange: handleInputChange,
                    onClick: async (props) => localHandleClick(props),
                  })}
                </Grid>
              );
            })}
          </Grid>
          {!editAppointment && (
            <Typography variant="subtitle1" mt={1}>
              Note: Any, previously created appointments for the selected
              appointment type will get closed.
            </Typography>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};
export default AppointmentModal;
